import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { FormItem } from './FormItem';

import './DbSize.css';

const validInt = val => val === String(+val);

const convertValue = value => {
  let val = `${Math.round(value * 10) / 10}Mb`;
  if (value > 1024 * 1024 * 1024) {
    val = `${Math.round((value / 1024 / 1024 / 1024) * 10) / 10}Pt`;
  } else if (value > 1024 * 1024) {
    val = `${Math.round((value / 1024 / 1024) * 10) / 10}Tb`;
  } else if (value > 1024) {
    val = `${Math.round((value / 1024) * 10) / 10}Gb`;
  }
  return val;
};

export const DbSize = () => {
  const [formTable, setFormTable] = useState('10');
  const [formAdded, setFormAdded] = useState('1000');
  const [formSize, setFormSize] = useState('1');
  const [formStart, setFormStart] = useState(
    new Date().toISOString().substr(0, 10)
  );
  const [errors, setErrors] = useState({
    table: null,
    added: null,
    size: null,
    start: null,
  });
  const [data, setData] = useState([]);
  const calculate = () => {
    const invalidFormat = 'Invalid format';
    const err = { table: null, added: null, size: null };
    let inError = false;
    if (formTable && !validInt(formTable)) {
      err.table = invalidFormat;
      inError = true;
    }
    if (formAdded && !validInt(formAdded)) {
      err.added = invalidFormat;
      inError = true;
    }
    if (formSize && !validInt(formSize)) {
      err.size = invalidFormat;
      inError = true;
    }
    setErrors(err);
    if (inError) return;
    const currentDate = new Date(formStart);
    const currentDay = currentDate.getDate();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const d = new Array(60).fill(0).map((_, i) => {
      const month = (currentMonth + i + 1) % 12;
      const year = currentYear + Math.floor((currentMonth + i + 1) / 12);
      const lastDay = new Date(year, month + 1, 0).getDate();
      const day = Math.min(currentDay, lastDay);
      const date = new Date(Date.UTC(year, month, day));
      const name = date.toLocaleDateString('en-EN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      const rowSize = 0.0012;
      const delay = (date.getTime() - currentDate.getTime()) / 24 / 3600 / 1000;
      return {
        name,
        size: +formSize * 1024 + +formAdded * +formTable * rowSize * delay,
      };
    });
    setData(d);
  };

  useEffect(calculate, [formSize, formTable, formAdded, formStart]);
  useEffect(calculate, []);

  return (
    <div className="App">
      <div className="Form">
        <FormItem
          type="select"
          label="Number of table"
          options={[
            { value: 10, label: '10' },
            { value: 20, label: '20' },
            { value: 50, label: '50' },
            { value: 100, label: '100' },
          ]}
          value={formTable}
          onChange={setFormTable}
          error={errors.table}
        />
        <FormItem
          type="select"
          label="Number of rows added each day"
          options={[
            { value: 1000, label: '1000' },
            { value: 10000, label: '10k' },
            { value: 100000, label: '100k' },
            { value: 1000000, label: '1M' },
            { value: 10000000, label: '10M' },
          ]}
          value={formAdded}
          onChange={setFormAdded}
          error={errors.added}
        />
        <FormItem
          type="select"
          label="Database size"
          options={[
            { value: 1, label: '1Gb' },
            { value: 10, label: '10Gb' },
            { value: 100, label: '100Gb' },
            { value: 1024, label: '1Tb' },
            { value: 10000, label: '100Tb' },
          ]}
          value={formSize}
          onChange={setFormSize}
          error={errors.size}
        />
        <FormItem
          type="date"
          label="Start estimation"
          value={formStart}
          onChange={setFormStart}
          error={errors.start}
        />
      </div>
      <BarChart
        width={840}
        height={600}
        data={data}
        margin={{
          top: 10,
          right: 30,
          left: 30,
          bottom: 0,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name" />
        <YAxis tickFormatter={convertValue} />
        <Tooltip formatter={value => [convertValue(value), 'Database size']} />
        <Bar type="monotone" dataKey="size" stroke="#67527A" fill="#896da1" />
      </BarChart>
    </div>
  );
};
